import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AppRoutes, ProfileRoutes, UserModel, UserStateService } from '@core';
import { AuthService } from '@auth';
import { SzBrandBlockComponent, SzIconComponent, SzTextInputComponent } from '@ui';

interface EditFormShape {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
}

@Component({
  selector: 'app-profile-edit-page',
  templateUrl: './profile-edit.page.html',
  styleUrls: ['./profile-edit.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, SzBrandBlockComponent, SzIconComponent, SzTextInputComponent]
})
export class ProfileEditPage {
  readonly profilePath: string = `/${AppRoutes.PROFILE}`;

  readonly loading: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);

  private readonly userState: UserStateService = inject(UserStateService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly router: Router = inject(Router);
  private readonly translateService: TranslateService = inject(TranslateService);

  readonly email: Signal<string> = computed((): string => {
    const e: string = (this.userState.user()?.email ?? '').trim();
    return e.length > 0 ? e : '-';
  });

  readonly form: FormGroup<EditFormShape>;

  constructor() {
    const user: UserModel | null = this.userState.user();
    const displayName: string = (user?.displayName ?? '').trim();
    const parts: ReadonlyArray<string> = displayName.split(/\s+/).filter((p: string): boolean => p.length > 0);
    const initialFirst: string = (user?.firstName ?? parts[0] ?? '').trim();
    const initialLast: string = (user?.lastName ?? parts.slice(1).join(' ')).trim();

    this.form = new FormGroup<EditFormShape>({
      firstName: new FormControl<string>(initialFirst, {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2)]
      }),
      lastName: new FormControl<string>(initialLast, {
        nonNullable: true,
        validators: [Validators.required, Validators.minLength(2)]
      })
    });
  }

  firstNameError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.firstName;
    if (ctrl.touched && ctrl.invalid) return this.translateService.instant('auth.errors.nameMin');
    return null;
  }

  lastNameError(): string | null {
    const ctrl: FormControl<string> = this.form.controls.lastName;
    if (ctrl.touched && ctrl.invalid) return this.translateService.instant('auth.errors.nameMin');
    return null;
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);
    const { firstName, lastName } = this.form.getRawValue();
    const trimmedFirst: string = firstName.trim();
    const trimmedLast: string = lastName.trim();
    const displayName: string = `${trimmedFirst} ${trimmedLast}`.trim();
    try {
      await this.userState.patchMe({ firstName: trimmedFirst, lastName: trimmedLast });
      try {
        await this.authService.updateDisplayName(displayName);
      } catch {}
      await this.router.navigateByUrl(this.profilePath);
    } catch (err: unknown) {
      const message: string = err instanceof Error ? err.message : 'Update failed';
      this.errorMessage.set(message);
    } finally {
      this.loading.set(false);
    }
  }

  goBack(): void {
    void this.router.navigateByUrl(this.profilePath);
  }

  onDeleteAccount(): void {
    // placeholder - to be implemented (BE endpoint + Firebase delete)
    // eslint-disable-next-line no-alert
    alert(this.translateService.instant('profileEdit.deleteAccountPlaceholder'));
  }

  // expose ProfileRoutes for template use if needed later
  protected readonly ProfileRoutes: typeof ProfileRoutes = ProfileRoutes;
}
